import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyId } from './useCompanyId';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { findAccountIdByEmail } from '../services/firestoreService';
import {
  fetchProgramDeliverablesBundle,
  getAccountStrategyCallPeriod,
  getCurrentPeriod,
  isMonthlyDeliverablePeriod,
} from '../utils/accountProgramDeliverables';
import { isHiringServiceItem } from '../utils/programPricing';

const logPortalDeliverableUseFn = httpsCallable(functions, 'logPortalDeliverableUse');

const EMPTY = { loading: false, hasDeliverable: false, limit: 0, used: 0, remaining: 0 };

/**
 * Shared hook for the company's Hiring service deliverable.
 * Resolves the client's account, then reports whether their program includes a
 * Hiring deliverable and how much of it is still available this billing period.
 *
 * Also exposes `logCompletion`, which records a hiring-service usage against the
 * account's deliverable progress — the same `account.deliverables[id].logs` shape
 * the CRM Client Overview reads — so a portal position request counts as one
 * completed hiring service and the remaining balance ticks down everywhere.
 *
 * @returns {{ loading: boolean, hasDeliverable: boolean, limit: number, used: number, remaining: number, logCompletion: (opts?: { note?: string }) => Promise<boolean> }}
 */
// overrideAccountId lets an internal viewer (CRM Portal View) resolve the
// deliverable for a specific client account instead of the logged-in user's.
export const useHiringDeliverable = (overrideAccountId = null) => {
  const { currentUser } = useAuth();
  const { companyId: resolvedCompanyId } = useCompanyId();
  const companyId = overrideAccountId || resolvedCompanyId;

  const [state, setState] = useState({ ...EMPTY, loading: true });

  // Live copies of what `logCompletion` needs, kept in refs so the action stays
  // stable and always writes against the latest account snapshot.
  const accountRef = useRef(null);
  const accountIdRef = useRef(null);
  const hiringItemsRef = useRef([]);

  useEffect(() => {
    let active = true;
    let unsubscribe = null;

    const hydrate = async (account) => {
      try {
        const { programItems } = await fetchProgramDeliverablesBundle(account);
        const hiringItems = (programItems || []).filter(isHiringServiceItem);
        const limit = hiringItems.reduce((sum, i) => sum + (Number(i.programQuantity) || 0), 0);
        const used = hiringItems.reduce((sum, i) => sum + (Number(i.completed) || 0), 0);
        accountRef.current = account;
        hiringItemsRef.current = hiringItems;
        if (!active) return;
        setState({
          loading: false,
          hasDeliverable: hiringItems.length > 0,
          limit,
          used,
          remaining: Math.max(0, limit - used),
        });
      } catch (err) {
        console.error('Error loading hiring deliverable:', err);
        if (active) setState({ ...EMPTY });
      }
    };

    const setup = async () => {
      if (!currentUser?.email && !companyId) {
        setState({ ...EMPTY });
        return;
      }

      const accountId =
        companyId ||
        (currentUser?.email ? await findAccountIdByEmail(currentUser.email, 'Clients') : null);

      if (!active) return;

      if (!accountId) {
        accountIdRef.current = null;
        setState({ ...EMPTY });
        return;
      }

      accountIdRef.current = accountId;

      unsubscribe = onSnapshot(
        doc(db, 'accounts', accountId),
        (snap) => {
          if (!snap.exists()) {
            accountRef.current = null;
            hiringItemsRef.current = [];
            if (active) setState({ ...EMPTY });
            return;
          }
          hydrate({ id: snap.id, ...snap.data() });
        },
        (err) => {
          console.error('Error subscribing to hiring deliverable account:', err);
          if (active) setState({ ...EMPTY });
        }
      );
    };

    setup();

    return () => {
      active = false;
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser?.email, companyId]);

  /**
   * Record one used hiring service against the account's deliverable progress.
   * Appends a completion log (quantity 1) to the hiring deliverable row, mirroring
   * the CRM's `handleDeliverableUpdate` so the completion shows up in Client
   * Overview and the client's remaining balance drops by one. Returns false if
   * there is no available hiring deliverable to consume.
   */
  const logCompletion = useCallback(async ({ note } = {}) => {
    const account = accountRef.current;
    const accountId = accountIdRef.current;
    const hiringItems = hiringItemsRef.current || [];
    if (!account || !accountId || hiringItems.length === 0) return false;

    // Consume from the first hiring row that still has room, else the first row.
    const target =
      hiringItems.find((i) => (Number(i.programQuantity) || 0) > (Number(i.completed) || 0)) ||
      hiringItems[0];
    const rowId = target.id;
    const catalogId = target.deliverableCatalogId;

    const period = target.deliverable?.period || target.deliverable?.frequency || 'One Time';
    const billingPeriod = isMonthlyDeliverablePeriod(period)
      ? getAccountStrategyCallPeriod(account, null)
      : null;
    const currentPeriod = billingPeriod ? billingPeriod.periodKey : getCurrentPeriod(period);

    // Usage logs decide what the client has left, so the server appends the log (one use,
    // on the caller's own account) and folds any legacy catalog-keyed slice into the row.
    await logPortalDeliverableUseFn({
      rowId: String(rowId),
      catalogId: catalogId ? String(catalogId) : null,
      period: currentPeriod,
      note: note || 'Position search requested via portal',
    });

    return true;
  }, []);

  return { ...state, logCompletion };
};
